import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Modal, Table } from "react-bootstrap";
import { FaExpand, FaCopy, FaMapMarkerAlt, FaVial, FaSatellite } from "react-icons/fa";
import { MapContainer, TileLayer, Polygon, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { hasPermission } from "../../hooks/usePermission";
import { hasUsableBoundary } from "../../Pages/Support/FarmSatellite/satelliteModel";

function FitBounds({ positions, maxZoom = 17, paddingX = 12, paddingY = 12 }) {
  const map = useMap();

  useEffect(() => {
    if (!positions || positions.length < 3) return;

    const bounds = L.latLngBounds(positions);

    map.fitBounds(bounds, {
      paddingX,
      paddingY,
      maxZoom,
      animate: false,
    });
  }, [map, positions, maxZoom, paddingX, paddingY]);

  return null;
}

const FarmMapThumb = ({ points, onOpen }) => {
  if (!points || points.length < 3) return <span className="text-muted">N/A</span>;

  const positions = points.map(p => [p.latitude ?? p.lat, p.longitude ?? p.lng]);
  const center = [
    positions.reduce((s, p) => s + p[0], 0) / positions.length,
    positions.reduce((s, p) => s + p[1], 0) / positions.length,
  ];

  return (
    <button
      onClick={onOpen}
      title="Click to view farm boundary on map"
      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', position: 'relative', display: 'inline-block' }}
    >
      <div style={{ width: 130, height: 85, borderRadius: 6, overflow: 'hidden', border: '2px solid #113460', pointerEvents: 'none' }}>
        <MapContainer
          center={center}
          zoom={14}
          style={{ width: '100%', height: '100%' }}
          scrollWheelZoom={false}
          dragging={false}
          zoomControl={false}
          attributionControl={false}
          keyboard={false}
          doubleClickZoom={false}
        >
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" maxZoom={19} />
          <Polygon positions={positions} pathOptions={{ fillColor: '#199ee0', fillOpacity: 0.25, color: '#113460', weight: 2 }} />
          <FitBounds
            positions={positions}
            maxZoom={18}
            paddingX={5}
            paddingY={5}
          />
        </MapContainer>
      </div>
      <span style={{
        position: 'absolute', bottom: 4, right: 4, background: 'rgba(0,0,0,0.55)', 
        borderRadius: 4, padding: '2px 4px', color: '#fff', fontSize: 10,
        display: 'flex', alignItems: 'center', gap: 3,
      }}>
        <FaExpand size={9} /> View
      </span>
    </button>
  );
};

const FarmMapModal = ({ farm, onClose }) => {
  const points = farm.geo_location?.data || [];
  const positions = points
    .map(p => [p.latitude ?? p.lat, p.longitude ?? p.lng])
    .filter(p => !isNaN(p[0]) && !isNaN(p[1]));

  const center = positions.length > 0
    ? [
        positions.reduce((s, p) => s + p[0], 0) / positions.length,
        positions.reduce((s, p) => s + p[1], 0) / positions.length,
      ]
    : [20.5937, 78.9629];

  return (
    <Modal show onHide={onClose} size="lg" centered>
      <Modal.Header closeButton style={{ borderBottom: '1px solid #e5e7eb', paddingBottom: 12 }}>
        <div>
          <Modal.Title style={{ fontSize: 18, fontWeight: 700, color: '#111827' }}>
            {farm.farm_name}
          </Modal.Title>
          {farm.farm_size && (
            <p style={{ margin: '2px 0 0', fontSize: 13, color: '#6b7280' }}>
              {farm.farm_size} {farm.farm_size_unit || ''} · Farm Boundary
            </p>
          )}
        </div>
      </Modal.Header>
      <Modal.Body style={{ padding: 0, borderRadius: '0 0 8px 8px', overflow: 'hidden' }}>
        <MapContainer center={center} zoom={14} style={{ width: '100%', height: 460 }} scrollWheelZoom zoomControl>
          <TileLayer
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            attribution="Tiles &copy; Esri"
            maxNativeZoom={18} maxZoom={22}
          />
          {positions.length >= 3 && (
            <>
              <Polygon positions={positions} pathOptions={{ fillColor: '#199ee0', fillOpacity: 0.25, color: '#113460', weight: 2 }} />
              <FitBounds positions={positions} maxZoom={18} paddingX={40} paddingY={40} />
            </>
          )}
        </MapContainer>
      </Modal.Body>
    </Modal>
  );
};

const LinkedSamplesModal = ({ farm, onClose }) => {
  return (
    <Modal show onHide={onClose} centered size="sm">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: '1.1rem' }}>
          Linked Samples
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small mb-3">Farm: <strong>{farm.farm_name}</strong></p>
        <div className="d-flex flex-column gap-2">
          {farm.linked_samples && farm.linked_samples.length > 0 ? (
            farm.linked_samples.map((sampleId) => (
              <div 
                key={sampleId} 
                className="d-flex justify-content-between align-items-center p-2 border rounded bg-light"
              >
                <span className="fw-semibold font-monospace text-primary">{sampleId}</span>
              </div>
            ))
          ) : (
            <p className="text-muted text-center mb-0 py-3">No samples linked.</p>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

function FarmTable({ farms, startIndex = 0 }) {
  const navigate = useNavigate();
  const canViewSatellite = hasPermission("farms:satellite");
  const [viewingFarm, setViewingFarm] = useState(null);
  const [selectedSamplesFarm, setSelectedSamplesFarm] = useState(null);
  const [copiedFarmId, setCopiedFarmId] = useState(null);

  const getFarmCenter = (farm) => {
    const points = farm.geo_location?.data;
    if (!points || points.length === 0) return null;
    const lats = points.map(p => p.latitude ?? p.lat).filter(v => v != null && !isNaN(v));
    const lngs = points.map(p => p.longitude ?? p.lng).filter(v => v != null && !isNaN(v));
    if (lats.length === 0) return null;
    return {
      lat: (lats.reduce((a, b) => a + b, 0) / lats.length).toFixed(6),
      lng: (lngs.reduce((a, b) => a + b, 0) / lngs.length).toFixed(6),
    };
  };

  const handleCopyCoords = (farm) => {
    const center = getFarmCenter(farm);
    if (!center) return;
    navigator.clipboard.writeText(`${center.lat}, ${center.lng}`).then(() => {
      setCopiedFarmId(farm.farm_id);
      setTimeout(() => setCopiedFarmId(null), 2000);
    });
  };

  return (
    <>
      <div className="table-responsive">
        <Table striped bordered hover className={`text-center align-middle ${farms && farms.length > 1 ? "align-middle" : ""}`}>
          <thead className="table-dark align-top">
            <tr>
              <th>S. NO.</th>
              <th>FARM NAME</th>
              <th>SIZE</th>
              <th>FARMER NAME</th>
              <th className="d-none d-md-table-cell">ADDED BY</th>
              <th>LINKED SAMPLES</th> {/* NEW COLUMN */}
              <th>GPS LOCATION</th>
              <th>GEO-FENCE</th>
            </tr>
          </thead>
          <tbody>
            {farms && farms.length > 0 ? (
              farms.map((farm, index) => {
                const center = getFarmCenter(farm);
                const coordsText = center ? `${center.lat}, ${center.lng}` : null;
                const sampleCount = farm.linked_samples ? farm.linked_samples.length : 0;

                return (
                  <tr key={farm.farm_id}>
                    <td>{startIndex + index + 1}</td>
                    <td className="fw-bold">{farm.farm_name?.toUpperCase()}</td>
                    <td>{farm.farm_size} {farm.farm_size_unit?.toUpperCase()}</td>
                    <td>{farm.owned_by_name || '-'}</td>
                    <td className="d-none d-md-table-cell">{farm.added_by_name || '-'}</td>
                    
                    {/* LINKED SAMPLES DATA */}
                    <td>
                      {sampleCount > 0 ? (
                        <button
                          className="btn btn-outline-primary btn-sm d-flex align-items-center justify-content-center mx-auto gap-1"
                          onClick={() => setSelectedSamplesFarm(farm)}
                          title="View Linked Samples"
                        >
                          <FaVial size={12} />
                          <span>View ({sampleCount})</span>
                        </button>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>

                    <td style={{ minWidth: 160 }}>
                      {center ? (
                        <div style={{ fontSize: 12 }}>
                          <div style={{ fontFamily: 'monospace', marginBottom: 4 }}>
                            <div><span style={{ color: '#6b7280' }}>Lat:</span> {center.lat}</div>
                            <div><span style={{ color: '#6b7280' }}>Lng:</span> {center.lng}</div>
                          </div>
                          <div className="d-flex gap-1 justify-content-center">
                            <button
                              onClick={() => handleCopyCoords(farm)}
                              title="Copy coordinates"
                              style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: 4, padding: '2px 6px', cursor: 'pointer', fontSize: 11, color: copiedFarmId === farm.farm_id ? '#16a34a' : '#374151' }}
                            >
                              <FaCopy size={10} className="me-1" />
                              {copiedFarmId === farm.farm_id ? 'Copied!' : 'Copy'}
                            </button>
                            <a
                              href={`https://www.google.com/maps?q=${coordsText}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="Open in Google Maps"
                              style={{ border: '1px solid #93c5fd', borderRadius: 4, padding: '2px 6px', fontSize: 11, color: '#1d4ed8', textDecoration: 'none', background: '#eff6ff' }}
                            >
                              <FaMapMarkerAlt size={10} className="me-1" /> Maps
                            </a>
                          </div>
                        </div>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td style={{ minWidth: 140 }}>
                      <FarmMapThumb points={farm.geo_location?.data} onOpen={() => setViewingFarm(farm)} />
                      {canViewSatellite && hasUsableBoundary(farm) && (
                        <button
                          type="button"
                          className="btn btn-outline-success btn-sm d-flex align-items-center justify-content-center mx-auto mt-1 gap-1"
                          onClick={() => navigate(`/farms/${encodeURIComponent(farm.farm_id)}/satellite`, { state: { farm } })}
                          title="View satellite insights"
                        >
                          <FaSatellite size={12} />
                          <span>Satellite</span>
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td className="no-entries py-4" colSpan="8">
                  No Farms Found...
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {viewingFarm && (
        <FarmMapModal farm={viewingFarm} onClose={() => setViewingFarm(null)} />
      )}

      {selectedSamplesFarm && (
        <LinkedSamplesModal 
          farm={selectedSamplesFarm} 
          onClose={() => setSelectedSamplesFarm(null)} 
        />
      )}
    </>
  );
}

export default FarmTable;